import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { type ShiftBlockWithMappedDetails } from "./api/useFetchPaginatedShiftBlocks";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  modalState: UseModalState;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  shiftBlockId: string;
  facilityId: string;
  orientationBlockId?: string;
  onConfirmBooking: () => void;
}

export function BookShiftBlockBottomSheet(props: Props) {
  const { modalState, shifts, shiftBlockId, facilityId, orientationBlockId, onConfirmBooking } =
    props;
  const { path: currentRouterUrl } = useRouteMatch();

  const isOrientationBlock = isDefined(orientationBlockId);

  const text = isOrientationBlock
    ? "Complete every shift in this block to finish the orientation. Missing a shift means you have to start over."
    : `You should only book this block if you are 100% committed to working all ${shifts.length} of the shifts in this block and to arriving on time for each of these shifts.`;

  const handleCancelClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.DISCARD_BLOCK_BOOKING_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts: shifts.length,
      location: currentRouterUrl,
    });
    modalState.closeModal();
  }, [currentRouterUrl, facilityId, modalState, shiftBlockId, shifts.length]);

  const handleBookClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.CONFIRM_BOOK_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts: shifts.length,
      location: currentRouterUrl,
    });
    modalState.closeModal();
    onConfirmBooking();
  }, [currentRouterUrl, facilityId, modalState, onConfirmBooking, shiftBlockId, shifts]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="horizontal">
          <Button fullWidth variant="outlined" size="large" onClick={handleCancelClicked}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" size="large" onClick={handleBookClicked}>
            Book
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            Book This Block
          </Text>
          <Illustration type="warning" />
          <Text align="center">{text}</Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
